// src/pages/Concierge/ConciergeComponents/ConciergeStep.jsx
import React, { useMemo, useState, useRef } from "react";
import axios from "axios";
import StepShell from "./StepComponents/StepShell";
import Step1 from "./StepComponents/Step1";
import Step2 from "./StepComponents/Step2";
import Step3 from "./StepComponents/Step3";
import { loadDaumPostcode } from "../../../../utils/loadDaumPostcode";
import { useCategories } from "../../../../hooks/useCategories";

function getMediaSummary(form) {
    const images = Array.isArray(form?.images) ? form.images : [];
    const videos = Array.isArray(form?.videos) ? form.videos : [];
    const totalBytes = [...images, ...videos].reduce(
        (total, media) => total + Number(media?.file?.size || 0),
        0
    );
    return {
        imageCount: images.length,
        videoCount: videos.length,
        totalBytes,
        totalMb: (totalBytes / (1024 * 1024)).toFixed(1),
    };
}

function getServerErrorMessage(error) {
    const detail = error?.response?.data?.detail;
    if (typeof detail === "string" && detail.trim()) return detail.trim();
    if (Array.isArray(detail)) {
        return detail
            .map((item) => item?.msg)
            .filter(Boolean)
            .join(", ");
    }
    const data = error?.response?.data;
    if (typeof data?.msg === "string" && data.msg.trim()) return data.msg.trim();
    if (typeof data?.message === "string" && data.message.trim()) {
        return data.message.trim();
    }
    return "";
}

async function reportConciergeSubmitError(payload) {
    try {
        await axios.post(
            `${process.env.REACT_APP_FASTAPI_ADS_URL}/submit/concierge/client-error`,
            payload,
            { timeout: 4000 }
        );
    } catch (reportError) {
        console.error("[submit/concierge] client-error report failed:", reportError);
    }
}

export default function ConciergeStep() {
    const [step, setStep] = useState(1);
    const [loadingNext, setLoadingNext] = useState(false);
    const [submissionSession, setSubmissionSession] = useState(null);

    const [form, setForm] = useState({
        // Step1
        name: "", phone: "", pin: "",
        // Step2
        storeName: "", roadAddress: "", addressDetail: "",
        // Step3
        menus: ["", "", ""],
        images: [], // [{id, url, file}]
        videos: [], // [{id, url, file, duration}]
    });
    const patch = (p) => setForm((prev) => ({ ...prev, ...p }));
    const updateVideoUploadState = (videoId, changes) => {
        setForm((prev) => ({
            ...prev,
            videos: (prev.videos || []).map((video) =>
                video.id === videoId ? { ...video, ...changes } : video
            ),
        }));
    };

    const {
        mainCategory, setMainCategory, mainCategories,
        subCategory, setSubCategory, subCategories,
        detailCategory, setDetailCategory, detailCategories
    } = useCategories();

    const hasAllCategoriesSelected = [
        mainCategory,
        subCategory,
        detailCategory,
    ].every((value) => {
        const normalized = String(value || "").trim();
        return !["", "0", "대분류", "중분류", "소분류"].includes(normalized);
    });

    // src/pages/Concierge/ConciergeComponents/ConciergeStep.jsx

    const canNext = useMemo(() => {
        if (step === 1) {
            const name = String(form.name || "").trim();
            const phoneDigits = String(form.phone || "").replace(/\D/g, "");
            const validName = name.length >= 2;                  // 이름 2글자 이상
            const validPhone = /^010\d{8}$/.test(phoneDigits);    // 010으로 시작하는 11자리
            const validPin = /^\d{6}$/.test(form.pin);           // 6자리 숫자 PIN
            return validName && validPhone && validPin;
        }
        if (step === 2)
            return form.storeName && form.roadAddress && hasAllCategoriesSelected;

        if (step === 3) {
            const hasMenu = (form.menus || []).some(
                (v) => (v || "").trim().length > 0
            );
            const hasImage = (form.images || []).length > 0;
            const hasVideo = (form.videos || []).length > 0;
            return hasMenu && (hasImage || hasVideo);
        }
        return false;
    }, [step, form, hasAllCategoriesSelected]);


    const goNext = () => setStep((s) => Math.min(3, s + 1));
    const goPrev = () => setStep((s) => Math.max(1, s - 1));

    const detailRef = useRef(null);
    const openPostcodePopup = async () => {
        await loadDaumPostcode();
        new window.daum.Postcode({
            oncomplete: (data) => {
                patch({
                    roadAddress: data.roadAddress || "",
                });
                setTimeout(() => detailRef.current?.focus?.(), 0);
            },
        }).open();
    };

    // ✅ “다음” 클릭 시: Step2면 서버 조회 → 성공 시 Step3
    const handleNext = async () => {
        if (!canNext || loadingNext) return;

        if (step !== 2) {
            goNext();
            return;
        }

        try {
            setLoadingNext(true);

            const payload = {
                store_name: String(form.storeName || "").trim(),
                road_address: String(form.roadAddress || "").trim(),
            };
            const { data } = await axios.post(
                `${process.env.REACT_APP_FASTAPI_ADS_URL}/is/concierge/store`,
                payload
            );
            // success=false면 메시지 보여주고 중단
            if (!data?.success) {
                alert(data?.message || "이미 등록 된 컨시어지 매장입니다.");
                return;
            }
            // 통과 → Step3
            setStep(3);
        } catch (err) {
            // 서버가 상태코드로 에러를 준 경우(예: 409)
            const msg =
                err?.response?.data?.detail ||
                err?.response?.data?.message ||
                "가게 검색 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.";
            console.error("[is/concierge] error:", err);
            alert(msg);
        } finally {
            setLoadingNext(false);
        }
    };


    const handleSubmit = async () => {
        if (!hasAllCategoriesSelected) {
            alert("업종 대분류, 중분류, 소분류를 모두 선택해 주세요.");
            return;
        }
        try {
            setLoadingNext(true);
            let activeSession = submissionSession;

            if (!activeSession) {
                const formData = new FormData();
                formData.append("mainCategory", mainCategory);
                formData.append("subCategory", subCategory);
                formData.append("detailCategory", detailCategory);

                Object.entries(form).forEach(([key, value]) => {
                    if (key === "images" || key === "videos") return;
                    if (value === undefined || value === null) return;
                    formData.append(key, value);
                });

                (form.images || []).forEach((image) => {
                    if (image?.file) formData.append("images", image.file);
                });

                const { data } = await axios.post(
                    `${process.env.REACT_APP_FASTAPI_ADS_URL}/submit/concierge`,
                    formData,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    }
                );

                if (!data?.success) {
                    const reason = data?.msg || "서버가 신청 정보를 저장하지 못했습니다.";
                    const media = getMediaSummary(form);
                    await reportConciergeSubmitError({
                        stage: "server_save",
                        status_code: 200,
                        error_code: "APPLICATION_REJECTED",
                        message: reason,
                        image_count: media.imageCount,
                        video_count: media.videoCount,
                        total_bytes: media.totalBytes,
                    });
                    alert(`컨시어지 신청 실패\n\n발생 단계: 서버 저장 처리\n원인: ${reason}`);
                    return;
                }

                activeSession = {
                    userId: data.user_id,
                    videoUploadToken: data.video_upload_token,
                };
                setSubmissionSession(activeSession);
            }

            const pendingVideos = (form.videos || []).filter(
                (video) => video.uploadStatus !== "completed"
            );
            if (pendingVideos.length && (
                !activeSession?.userId ||
                !activeSession?.videoUploadToken
            )) {
                throw Object.assign(
                    new Error("동영상 업로드 인증 정보를 받지 못했습니다."),
                    { conciergeStage: "동영상 업로드 준비" }
                );
            }

            for (const video of pendingVideos) {
                updateVideoUploadState(video.id, {
                    uploadStatus: "uploading",
                    uploadProgress: 0,
                    uploadError: "",
                });
                const videoFormData = new FormData();
                videoFormData.append("video", video.file);
                try {
                    await axios.post(
                        `${process.env.REACT_APP_FASTAPI_ADS_URL}/submit/concierge/${activeSession.userId}/video`,
                        videoFormData,
                        {
                            headers: {
                                "Content-Type": "multipart/form-data",
                                "X-Concierge-Video-Token": activeSession.videoUploadToken,
                            },
                            onUploadProgress: (event) => {
                                if (!event.total) return;
                                updateVideoUploadState(video.id, {
                                    uploadProgress: Math.min(
                                        99,
                                        Math.round((event.loaded * 100) / event.total)
                                    ),
                                });
                            },
                        }
                    );
                    updateVideoUploadState(video.id, {
                        uploadStatus: "completed",
                        uploadProgress: 100,
                    });
                } catch (videoError) {
                    const reason =
                        getServerErrorMessage(videoError) ||
                        "동영상 전송 중 오류가 발생했습니다.";
                    updateVideoUploadState(video.id, {
                        uploadStatus: "failed",
                        uploadError: reason,
                    });
                    videoError.conciergeStage = "동영상 개별 업로드";
                    throw videoError;
                }
            }

            // ✅ 성공 시: 안내 후, wizmarket_ai 리다이렉트
            alert("신청이 접수되었습니다! 담당자가 곧 연락 드리겠습니다");
            window.location.href = "https://wizmarket.ai/";

        } catch (err) {
            console.error("[submit/concierge] error:", err);
            const statusCode = err?.response?.status;
            const media = getMediaSummary(form);
            const serverMessage = getServerErrorMessage(err);
            let stage = err?.conciergeStage || "server_response";
            let reason = serverMessage || err?.message;

            if (err?.conciergeStage) {
                reason = serverMessage || err?.message || "처리 중 오류가 발생했습니다.";
            } else if (statusCode === 413) {
                stage = "첨부파일 전송";
                reason =
                    `첨부 파일의 총 용량(${media.totalMb}MB)이 서버의 현재 업로드 허용 용량을 초과했습니다.`;
            } else if (statusCode === 409) {
                stage = "중복 등록 확인";
                reason = serverMessage || "이미 등록된 컨시어지 매장입니다.";
            } else if (statusCode === 400 || statusCode === 422) {
                stage = "입력값 검증";
                reason = serverMessage || "입력값 또는 첨부파일 형식이 올바르지 않습니다.";
            } else if (statusCode >= 500) {
                stage = "서버 처리";
                reason =
                    serverMessage ||
                    `서버 내부 오류가 발생했습니다. (HTTP ${statusCode})`;
            } else if (err?.code === "ECONNABORTED") {
                stage = "서버 응답 대기";
                reason = "업로드 처리 시간이 초과되었습니다.";
            } else if (!err?.response) {
                stage = "서버 연결";
                reason = "서버에 연결할 수 없습니다. 네트워크 상태를 확인해 주세요.";
            } else {
                reason =
                    serverMessage ||
                    `알 수 없는 응답 오류가 발생했습니다. (HTTP ${statusCode || "-"})`;
            }

            await reportConciergeSubmitError({
                stage,
                status_code: statusCode || null,
                error_code: err?.code || null,
                message: reason,
                image_count: media.imageCount,
                video_count: media.videoCount,
                total_bytes: media.totalBytes,
            });

            alert(`컨시어지 신청 실패\n\n발생 단계: ${stage}\n원인: ${reason}`);
        } finally {
            setLoadingNext(false);
        }
    };



    return (
        <StepShell
            step={step}
            total={3}
            onPrev={goPrev}
            onNext={handleNext}           // ⬅️ 여기만 교체
            canNext={canNext && !loadingNext} // 로딩 시 비활성화
            onSubmit={handleSubmit}
        >
            {step === 1 && <Step1 data={form} update={patch} />}

            {step === 2 && (
                <Step2
                    data={{ ...form, onSearchAddress: openPostcodePopup, detailRef }}
                    update={patch}
                    categoryState={{
                        mainCategory, setMainCategory, mainCategories,
                        subCategory, setSubCategory, subCategories,
                        detailCategory, setDetailCategory, detailCategories
                    }}
                />
            )}

            {step === 3 && <Step3 data={form} update={patch} />}
        </StepShell>
    );
}
